import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { URL } from "../../core/config";

import { useSemester } from "../../core/SemesterContext";
const usersURL = URL + "/users";

function idKey(id) {
  if (!id) return "";
  if (typeof id === "object") return String(id._id || id);
  return String(id);
}

function useUsers(options = {}) {
  const semesterContext = useSemester();
  const semester = semesterContext.currentSemester;
  const { enablePagination = false, usersPerPage = 20, resolveIds = [] } = options;
  const resolveKey = [...new Set(resolveIds.map(idKey).filter(Boolean))].sort().join(",");

  // Track if hook is mounted
  const isMountedRef = useRef(true);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Cache for all semester users (active roster — Unbound members are not included)
  const [allSemesterUsers, setAllSemesterUsers] = useState([]);
  const [semesterUsersLoaded, setSemesterUsersLoaded] = useState(false);
  // People already attached to a record (event staff, and so on) who are no longer
  // on the active roster. Kept separate so pickers and directories stay active-only.
  const [resolvedUsers, setResolvedUsers] = useState([]);
  const resolvedIdsRef = useRef(new Set());

  // Fetch all semester users once for real-time search
  const fetchAllSemesterUsers = useCallback(async () => {
    if (!semester?._id) return;

    try {
      if (isMountedRef.current) {
        setLoading(true);
      }

      const params = new URLSearchParams();
      params.append("semester", semester._id);
      params.append("includeSuspended", true);

      const response = await axios.get(`${usersURL}/list?${params.toString()}`);
      const usersData = response.data || [];

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setAllSemesterUsers(usersData);
        setSemesterUsersLoaded(true);
        setTotalUsers(usersData.length);
        setError(null);
      }
    } catch (err) {
      console.error("Error fetching semester users:", err);
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setError(err);
        setAllSemesterUsers([]);
      }
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [semester?._id]);

  const loadUnboundUsers = useCallback(async () => {
    if (!semester?._id) return [];

    const params = new URLSearchParams();
    params.set("semester", semester._id);
    params.set("includeSuspended", "true");
    params.set("membershipType", "Unbound");

    const response = await axios.get(`${usersURL}/list?${params.toString()}`);
    return Array.isArray(response.data) ? response.data : [];
  }, [semester?._id]);

  const loadAlumniUsers = useCallback(async () => {
    if (!semester?._id) return [];

    const params = new URLSearchParams();
    params.set("semester", semester._id);
    params.set("includeSuspended", "true");
    params.set("membershipType", "Alumni");

    const response = await axios.get(`${usersURL}/list?${params.toString()}`);
    return Array.isArray(response.data) ? response.data : [];
  }, [semester?._id]);

  useEffect(() => {
    resolvedIdsRef.current = new Set();
    setResolvedUsers([]);
  }, [semester?._id]);

  useEffect(() => {
    if (!semesterUsersLoaded || !resolveKey) return;

    const rosterIds = new Set(allSemesterUsers.map((user) => idKey(user._id)));
    const missing = resolveKey
      .split(",")
      .filter((id) => id && !rosterIds.has(id) && !resolvedIdsRef.current.has(id));

    if (missing.length === 0) return;

    missing.forEach((id) => resolvedIdsRef.current.add(id));
    let cancelled = false;

    axios
      .get(`${usersURL}/by-ids`, { params: { ids: missing.join(",") } })
      .then((response) => response.data || [])
      .catch(() => [])
      .then((rows) => {
      if (cancelled) {
        missing.forEach((id) => resolvedIdsRef.current.delete(id));
        return;
      }

      const found = rows.filter((user) => user && user._id);
      if (found.length === 0) return;

      setResolvedUsers((prev) => {
        const byId = new Map(prev.map((user) => [idKey(user._id), user]));
        found.forEach((user) => byId.set(idKey(user._id), user));
        return Array.from(byId.values());
      });
    });

    return () => {
      cancelled = true;
    };
  }, [resolveKey, allSemesterUsers, semesterUsersLoaded]);

  // Helper function to format user display name
  const formatUserDisplayName = useCallback((user) => {
    const fullname = user.fullName || "";
    const firstLastName = user.firstLastName || "";
    const nickname = user.nickname || "";

    // console.log("Fullname: ", fullname);

    if (nickname && firstLastName) {
      const trimmedFirstLastName = firstLastName.trim();
      // console.log(`${nickname} (${trimmedFirstLastName})`);
      return `${nickname} (${trimmedFirstLastName})`;
    } else if (firstLastName) {
      const trimmedFirstLastName = firstLastName.trim();
      return `${trimmedFirstLastName}`;
    } else if (nickname && fullname) {
      const trimmedFullname = fullname.trim();
      // console.log(`${nickname} (${trimmedFullname})`);
      return `${nickname} (${trimmedFullname})`;
    }
    // console.log(fullname || nickname || "Unknown User");
    return fullname || "Unknown User";
  }, []);

  // Filter users function for UsersList component
  const filterUsers = useCallback(
    (options = {}) => {
      const {
        searchTerm = "",
        departments = [],
        showOnlySuspended = false,
        includeSuspended = false,
      } = options;

      let filteredUsers = [...allSemesterUsers];

      // Filter by search term
      if (searchTerm.trim()) {
        const searchLower = searchTerm.trim().toLowerCase();
        filteredUsers = filteredUsers.filter((user) => {
          const fullName = (user.fullName || "").toLowerCase();
          const nickname = (user.nickname || "").toLowerCase();

          return (
            fullName.includes(searchLower) || nickname.includes(searchLower)
          );
        });
      }

      // Filter by departments
      if (departments.length > 0) {
        filteredUsers = filteredUsers.filter((user) => {
          return user.departments?.some((dept) => departments.includes(dept));
        });
      }

      // Filter by suspension status
      if (showOnlySuspended) {
        filteredUsers = filteredUsers.filter(
          (user) => user.suspension?.isSuspended,
        );
      } else if (!includeSuspended) {
        filteredUsers = filteredUsers.filter(
          (user) => !user.suspension?.isSuspended,
        );
      }

      return filteredUsers;
    },
    [allSemesterUsers],
  );

  // Effect to fetch users when semester change
  useEffect(() => {
    if (semester?._id) {
      if (isMountedRef.current) {
        setAllSemesterUsers([]);
        setSemesterUsersLoaded(false);
        setLoading(true);
      }
      fetchAllSemesterUsers();
    }
  }, [semester?._id, fetchAllSemesterUsers]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Memoized user options for react-select
  const userOptions = useMemo(() => {
    if (!Array.isArray(allSemesterUsers)) return [];
    const options = allSemesterUsers.map((user) => ({
      value: user._id,
      label: formatUserDisplayName(user),
      userData: user,
    }));
    return options;
  }, [allSemesterUsers, formatUserDisplayName]);

  const findUser = useCallback(
    (id) => {
      const key = idKey(id);
      if (!key) return undefined;
      return (
        allSemesterUsers.find((user) => idKey(user._id) === key) ||
        resolvedUsers.find((user) => idKey(user._id) === key)
      );
    },
    [allSemesterUsers, resolvedUsers]
  );

  // Active roster, plus anyone already selected who is no longer active,
  // so editing an existing record does not drop them from the selection.
  const optionsForSelection = useCallback(
    (selectedIds = []) => {
      const rosterIds = new Set(userOptions.map((option) => idKey(option.value)));
      const seen = new Set();
      const extras = (Array.isArray(selectedIds) ? selectedIds : [])
        .map((id) => findUser(id))
        .filter((user) => {
          if (!user) return false;
          const key = idKey(user._id);
          if (!key || rosterIds.has(key) || seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((user) => ({
          value: user._id,
          label: formatUserDisplayName(user),
          userData: user,
        }));

      return extras.length > 0 ? [...extras, ...userOptions] : userOptions;
    },
    [userOptions, findUser, formatUserDisplayName]
  );

  // searchUsers returns option objects for react-select: [{value: _id, label: "nickname (fullname)"}, ...]
  // it may be helpfull in the future
  const searchUsers = useCallback(
    async (inputValue) => {
      const searchTerm = inputValue?.trim().toLowerCase() || "";

      // If no search term, return all user options
      if (!searchTerm) {
        return userOptions;
      }

      // Filter userOptions based on search term
      return userOptions.filter((option) => {
        const label = option.label.toLowerCase();
        return label.includes(searchTerm);
      });
    },
    [userOptions],
  );

  // Pagination helpers
  const totalPages = enablePagination
    ? Math.ceil(totalUsers / usersPerPage)
    : 1;
  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, totalPages]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage]);

  return {
    // Data
    users: allSemesterUsers,
    userOptions,
    loading,
    error,
    totalUsers,
    semesterUsersLoaded,

    filterUsers,
    searchUsers,
    findUser,
    optionsForSelection,
    loadUnboundUsers,
    loadAlumniUsers,
    formatUserDisplayName,
    refreshUsers: fetchAllSemesterUsers,

    // Pagination
    currentPage,
    totalPages,
    goToNextPage,
    goToPreviousPage,
    setCurrentPage,
  };
}

export default useUsers;
